import { action, computed, observable } from 'mobx';
import i18next from 'i18next';
import Alert from 'react-s-alert';
import { isFunction } from 'lodash';
import { analytics } from 'core/analytics';
import { refreshAccessToken } from 'core/jwt';
import { Variables, Input, AppManager } from 'core/index';

import { clientStore } from 'apps/client/app';
import { InitiativeConnManager } from 'apps/initiative/connManager';
import { ProfileConnManager } from 'apps/profile/main/connManager';
import { ProjectConnManager } from 'apps/project/connManager';
import { ThreadConnManager } from 'apps/forum/main/appManager';
import { StoryConnManager } from 'apps/story/main/appManager';
import { PaymentSpaceApp } from 'apps/payment/app';
import { SubscriptionAppManager } from 'apps/payment/subscription/appManager';
import { CollectionConnManager } from 'apps/collection/main/connManager';
import { headerStore } from 'apps/components/header/app';

import { isNumber } from 'apps/utils';

import {
  ProfileMutation,
  ProfileQuery,
  ProfileFollowingCountQuery,
  FollowingProfilesConnectionQuery,
  FollowingOrganizationsConnectionQuery,
  FollowingProjectsConnectionQuery,
  BillingSubscriptionQuery,
  BanProfileMutation,
  DeleteProfileMutation,
  UpdatePromotionMutation,
  UpdateSpaceFlagMutation,
} from 'apps/profile/gql';
import { ExtendFreeTrialMutation } from 'apps/initiative/gql/index';
import { ProfileThreadsQuery } from 'apps/forum/gql';
import { ProfileModel, ProfileApp } from 'apps/profile/app';

const MAX_FREE_SEATS = 100;
const MAX_DISCOUNT = 100;

export class ProfileAppManager extends AppManager {
  static registryName = 'ProfileAppManager';
  // TODO: dispose of the queries/variables/subManagers when we dispose the appManager
  // TODO: dispose of apps too - for app specific stuff
  // TODO: dispose of Query when we replace it

  @observable billingDialogVisible = false;
  @observable deleteProfileDialogVisible = false;
  @observable deleteProfileVerification = null;

  @observable seats;

  @observable discount;

  @observable updatingBilling = false;
  @observable updatingFlags = false;
  @observable featureDialogVisible = false;

  @observable subscriptionQuery = null;

  @observable freeSeatsPreview = 0;
  @observable discountPreview = 0;
  @observable earlyAccess = false;
  @observable freeTrialExtending = false;

  constructor(parent, init = {}, options) {
    super(parent, init, options);
    this.AppRef('profile', ProfileApp);

    this.variables = new Variables({
      username: () => this.params.get('profile'),
      // projects
      projectsCursor: () => null,
      projectsCount: () => this.projectsConn.variables.batchSize,
      projectsSortBy: () => this.projectsConn.sortBy,
      // stories
      storiesCursor: () => null,
      storiesCount: () => {
        if (
          this.storiesConn.arrayApp.length > this.storiesConn.initialBatchSize
        ) {
          return this.storiesConn.batchSize;
        }
        return this.storiesConn.initialBatchSize;
      },
      storiesSortBy: () => this.storiesConn.sortBy,
      // threads
      threadCursor: () => this.threadsConn.endCursor,
      threadSortBy: () => this.threadsConn.sortBy,
      threadBatchSize: () => this.threadsConn.batchSize,
      // collections
      collectionsCursor: () => null,
      collectionsCount: () => {
        if (
          this.collectionsConn.arrayApp.length >
          this.collectionsConn.initialBatchSize
        ) {
          return this.collectionsConn.batchSize;
        }
        return this.collectionsConn.initialBatchSize;
      },
      collectionsSortBy: () => this.collectionsConn.sortBy,
      // followers
      followersCursor: () => null,
      followersCount: () => this.followersConn.batchSize,
      followersSortBy: () => this.followersConn.sortBy,
      // followingProfiles
      followingProfilesCursor: () => this.followingProfilesConn.endCursor,
      followingProfilesCount: () => this.followingProfilesConn.batchSize,
      followingProfilesSortBy: () => this.followingProfilesConn.sortBy,
      // followingInitiatives
      followingInitiativesCursor: () => this.followingInitiativesConn.endCursor,
      followingInitiativesCount: () => this.followingInitiativesConn.batchSize,
      followingInitiativesSortBy: () => this.followingInitiativesConn.sortBy,
      // followingProjects
      followingProjectsCursor: () => this.followingProjectsConn.endCursor,
      followingProjectsCount: () =>
        this.followingProjectsConn.variables.batchSize,
      followingProjectsSortBy: () => this.followingProjectsConn.sortBy,
    });

    this.initiativesConn = new InitiativeConnManager(this, {
      sortBy: 'recent',
    });
    this.followersConn = new ProfileConnManager(this, {
      sortBy: 'recent',
      initialBatchSize: 0,
    });
    this.followingProfilesConn = new ProfileConnManager(this, {
      sortBy: 'recent',
      initialBatchSize: 0,
      loadMoreQuery: FollowingProfilesConnectionQuery,
    });
    this.followingInitiativesConn = new InitiativeConnManager(this, {
      sortBy: 'recent',
      initialBatchSize: 0,
      loadMoreQuery: FollowingOrganizationsConnectionQuery,
    });
    this.followingProjectsConn = new ProjectConnManager(this, {
      sortBy: 'recent',
      initialBatchSize: 10,
      loadMoreQuery: FollowingProjectsConnectionQuery,
    });
    this.projectsConn = new ProjectConnManager(this, {
      sortBy: 'recent',
      initialBatchSize: 6,
      sortOptions: [
        ['recent', i18next.t('Recent')],
        ['followers', i18next.t('Followers')],
        ['contributions', i18next.t('Contributions')],
        ['pageviews', i18next.t('Views')],
      ],
    });
    this.storiesConn = new StoryConnManager(this, {
      sortBy: 'recent',
      initialBatchSize: 4,
    });
    this.threadsConn = new ThreadConnManager(this, {
      sortOptions: [],
      batchSize: 5,
      loadMoreQuery: ProfileThreadsQuery,
    });
    this.collectionsConn = new CollectionConnManager(this, {
      sortBy: 'recent',
    });

    this.profileInput = new Input({
      id: () => this.profile.id,
      username: () => this.profile.user.username,
      fullName: () => this.profile.fullName,
      bio: () => this.profile.bio,
      skills: () =>
        this.profile.skills.map(({ id, name, deleted }) => ({
          id,
          name,
          deleted,
        })),
      socialAccounts: () =>
        this.profile.socialAccounts.map(social => ({
          id: social.id,
          accountType: social.accountType,
          accountName: social.accountName,
          deleted: social.deleted,
        })),
      avatar: () => {
        if (this.profile.avatar) {
          return this.profile.avatar.id;
        }
        return null;
      },
    });
    this.variables.onChange(['username'], this.NewUsername);
  }

  @computed get loading() {
    return this.queries.get('profile')?.loading;
  }

  @action
  ExtendFreeTrial = (trialEnd, callback) => {
    const input = new Input({
      spaceId: () => this.profile.inSpace.id,
      trialEnd: () => `${trialEnd}`,
    });
    this.freeTrialExtending = true;

    const mutation = new ExtendFreeTrialMutation(
      input,
      null,
      {
        onSuccess: () => {
          this.freeTrialExtending = false;
          callback?.();
          Alert.success('Free trial extended successfully');
        },
        onSysErr: error => {
          this.freeTrialExtending = false;
          Alert.error(error?.message);
        },
        onUserErr: error => {
          this.freeTrialExtending = false;
          Alert.error(error?.message);
        },
      },
      true,
    );

    mutation.mutate();
  };

  @action NewUsername = () => {
    this.initiativesConn.NewUsername();
    this.projectsConn.NewUsername();
    this.storiesConn.NewUsername();
    this.collectionsConn.NewUsername();
    this.threadsConn.NewVariables();
    this.NewProfileQuery();
  };

  @action NewProfileQuery = () => {
    // dispose old queries via observe
    const q = new ProfileQuery(this, {
      queryKey: 'profile',
      onSysErr: this.onProfileError,
    });
    q.process(profile => ProfileModel.Load(profile), this.ProfileUpdate);
    q.process(this.initiativesConn.process, this.InitiativesUpdate, {
      subPath: 'initiatives',
    });
    q.process(this.followersConn.process, this.FollowersUpdate, {
      subPath: 'followers',
    });
    q.process(
      this.followingProfilesConn.process,
      this.FollowingProfilesUpdate,
      { subPath: 'followingProfiles' },
    );
    q.process(
      this.followingInitiativesConn.process,
      this.FollowingInitiativesUpdate,
      { subPath: 'followingInitiatives' },
    );
    q.process(
      this.followingProjectsConn.process,
      this.FollowingProjectsUpdate,
      { subPath: 'followingProjects' },
    );
    q.process(this.projectsConn.process, this.ProjectsUpdate, {
      subPath: 'contributedProjects',
    });
    q.process(this.storiesConn.process, this.StoriesUpdate, {
      subPath: 'stories',
    });
    q.process(this.collectionsConn.process, this.CollectionsUpdate, {
      subPath: 'collections',
    });
    q.process(this.threadsConn.process, this.ThreadsUpdate, {
      subPath: 'threads',
    });
    q.query();
  };

  @action Impersonate = async () => {
    await refreshAccessToken(clientStore, this.profile.user.id);

    clientStore.setIsImpersonating(true);

    window.location.replace('/');
  };

  @action FollowingUpdate = () => {
    const q = new ProfileFollowingCountQuery(this, {
      queryKey: 'following',
      onSysErr: this.onProfileError,
    });
    q.process(
      result => ProfileModel.Load(result),
      profile => {
        this.profile.followingCount = profile.followingCount;
      },
    );
    q.query();
  };

  @action
  ProfileUpdate = profileModel => {
    this.profile = profileModel;
    if (this.profile) {
      headerStore.SetPage(profileModel);
    }
  };

  @action InitiativesUpdate = (initiatives, query) => {
    this.initiativesConn.pushResultIfFirst(initiatives, query);
  };

  @action FollowersUpdate = (followers, query) => {
    this.followersConn.pushResultIfFirst(followers, query);
  };

  @action FollowingProfilesUpdate = (followingProfiles, query) => {
    this.followingProfilesConn.pushResultIfFirst(followingProfiles, query);
  };

  @action FollowingInitiativesUpdate = (followingInitiatives, query) => {
    this.followingInitiativesConn.pushResultIfFirst(
      followingInitiatives,
      query,
    );
  };

  @action FollowingProjectsUpdate = (followingProjects, query) => {
    this.followingProjectsConn.pushResultIfFirst(followingProjects, query);
  };

  @action ProjectsUpdate = (projects, query) => {
    this.projectsConn.pushResultIfFirst(projects, query);
  };

  @action StoriesUpdate = (stories, query) => {
    this.storiesConn.pushResultIfFirst(stories, query);
  };

  @action CollectionsUpdate = (collections, query) => {
    this.collectionsConn.pushResultIfFirst(collections, query);
  };

  @action ThreadsUpdate = (threads, query) => {
    this.threadsConn.pushResultIfFirst(threads, query);
  };

  // FIXME This code is repeated at least in apps/initiative/appManager
  @action Subscribe = params => {
    const subscriptionAppManager = new SubscriptionAppManager(this, params);
    const paymentSpace = new PaymentSpaceApp(subscriptionAppManager, params);
    if (!paymentSpace || (paymentSpace && !paymentSpace.preview)) {
      subscriptionAppManager.NewSubscriptionQuery({
        preview: true,
      });
    }
    paymentSpace.ConfirmSubscribe();
  };

  @action SetBanProfile = (profile, banned, options) => {
    const input = new Input({
      username: profile.user?.username.replace('@', ''),
      banned,
    });
    const mutation = BanProfileMutation.New(input, options);
    return mutation;
  };

  @action DeleteProfile = (profile, options) => {
    const input = new Input({
      username: profile.user?.username.replace('@', ''),
    });
    const mutation = DeleteProfileMutation.New(input, options);
    return mutation;
  };

  @action Consolidate = () => {
    const { profile } = this;
    if (!profile.editFormStore.isValid) {
      profile.editFormStore.alert();
    } else {
      profile.editFormStore.SetPosting(true);
      const options = {
        onSysErr: this.onSaveSysError,
        onUserErr: this.onSaveUserError,
        onSuccess: this.onSaveSuccess,
      };
      this.mutations.push(ProfileMutation.New(this.profileInput, options));
    }
  };

  Cancel = () => {
    this.profile.ResetLocals();
    this.profile.SetEditing(false);
  };

  Save = () => {
    this.profile.SetSaving(true);
    analytics.sendEvent('updated-profile', {}, { ga: true });
    const avatarField = this.profile.editFormStore.fieldStores.get('avatar');
    if (avatarField && avatarField.fileModified) {
      avatarField.getUpdate(this.Consolidate);
    } else {
      this.Consolidate();
    }
  };

  onSaveSysError = () => {
    this.profile?.SetSaving(false);
    Alert.error(
      i18next.t('Hmm... We failed to update your Profile. Please try again.'),
    );
    this.profile?.editFormStore.SetPosting(false);
  };

  onSaveUserError = errs => {
    this.profile?.SetSaving(false);
    this.profile?.editFormStore.SetPosting(false);
    this.profile?.editFormStore.SetServerErrs(errs);
  };

  onSaveSuccess = profile => {
    if (this.profile?.isOwnProfile) {
      const profileModel = ProfileModel.Load(profile);
      clientStore.SetSignedInProfile(profileModel);
    }
    this.profile?.SetEditing(false);
    this.profile?.SetSaving(false);
    this.profile?.ResetLocals();
    this.profile?.editFormStore.SetPosting(false);
    this.profile?.editFormStore.clearDraft();
    Alert.success(i18next.t('Your Profile has been updated successfully'));
  };

  onBanError = error => {
    analytics.logError(error);
    Alert.error(i18next.t('There was an error while banning the user'));
  };

  onBanSuccess = () => {
    Alert.info(i18next.t('User has been banned ⚡'));
    // - doing NewProfileQuery here to update the ban state and therefore update
    // the ui, should be done through the return from the mutation, but that does
    // not work for me locally
    // - TODO: check why management return from BanProfileMutation is not used for
    // updating the local state
    this.NewProfileQuery();
  };

  onUnBanSuccess = () => {
    Alert.info(i18next.t('User has been unbanned 🕊️'));
    this.NewProfileQuery();
  };

  UnBan = () => {
    this.SetBanProfile(this.profile, false, {
      onSysErr: this.onBanError,
      onUserErr: this.onBanError,
      onSuccess: this.onUnBanSuccess,
    });
  };

  Ban = () => {
    this.SetBanProfile(this.profile, true, {
      onSysErr: this.onBanError,
      onUserErr: this.onBanError,
      onSuccess: this.onBanSuccess,
    });
  };

  onDeleteError = error => {
    analytics.logError(error);
    Alert.error(i18next.t('There was an error while deleting the user'));
  };

  onDeleteSuccess = () => {
    Alert.info(i18next.t('User has been deleted 🗑'));
    this.NewProfileQuery();
  };

  Delete = () => {
    const username = this.profile.user?.username.replace('@', '');
    if (this.deleteProfileVerification !== username) {
      this.DeleteProfile(this.profile, {
        onSysErr: this.onDeleteError,
        onUserErr: this.onDeleteError,
        onSuccess: this.onDeleteSuccess,
      });
    } else {
      Alert.error(i18next.t('Wrong user verification string!'));
    }
  };

  @action SetDeleteProfileDialogVisible = visible => {
    this.deleteProfileVerification = null;
    this.deleteProfileDialogVisible = visible;
  };

  @action SetDeleteProfileVerification = value => {
    this.deleteProfileVerification = value;
  };

  @computed get cadDiff() {
    if (!this.profile.inSpace.flags) return null;

    return JSON.parse(this.profile.inSpace.flags).cadDiff;
  }

  @computed get featureFlags() {
    return JSON.parse(this.profile.inSpace.flags);
  }

  @action HandleFeatureFlagsByKey(featureKey, value) {
    const flags = JSON.parse(this.profile.inSpace.flags || '{}');

    flags[featureKey] = value;

    return flags;
  }

  @action UpdateFeatureFlags = option => {
    const { key, value, onSuccess, onError } = option || {};
    this.updatingFlags = true;

    const saveFlags = this.HandleFeatureFlagsByKey(key, value);

    const input = new Input({
      spaceId: this.profile.inSpace.id,
      flags: JSON.stringify(saveFlags),
    });

    const mutation = UpdateSpaceFlagMutation.New(input, {
      onSuccess: result => {
        this.updatingFlags = false;
        if (isFunction(onSuccess)) {
          onSuccess(result);
        }
      },
      onSysErr: () => {
        Alert.error('There was an error while updating flags');
        this.updatingFlags = false;
        if (isFunction(onError)) {
          onError();
        }
      },
      onUserErr: () => {
        Alert.error('There was an error while updating flags');
        this.updatingFlags = false;
        if (isFunction(onError)) {
          onError();
        }
      },
    });

    return mutation;
  };

  @action SetFeatureDialogVisible = visible => {
    this.featureDialogVisible = visible;
  };

  @action SetBillingDialogVisible = visible => {
    this.billingDialogVisible = visible;
  };

  @action QueryBillingSubscription = () => {
    this.subscriptionQuery = new BillingSubscriptionQuery(this, {
      variables: {
        username: this.profile.user.username,
      },
    });

    this.subscriptionQuery.process(
      profile => ProfileModel.Load(profile),
      result => {
        if (!result) {
          return;
        }

        this.ProfileUpdate(result);

        if (result.promotion) {
          this.SetDiscountPreviewNumber(result.promotion.discount);
          this.SetFreeSeatsPreviewNumber(result.promotion.freeSeats);
          this.SetEarlyAccess(result.promotion.earlyAccess);
        }
      },
    );

    this.subscriptionQuery.query();
  };

  @action SetDiscount = value => {
    const val = value ? Number(value) : '';

    if (val < 0 || val > MAX_DISCOUNT) {
      return;
    }

    this.discount = val;
    this.seats = '';
  };

  @action SetSeats = value => {
    const val = value ? Number(value) : '';

    if (val < 0 || val > MAX_FREE_SEATS) {
      return;
    }

    this.seats = val;
    this.discount = '';
  };

  @action SubmitPromotionData = () => {
    const data = {};

    if (isNumber(this.seats)) {
      data.freeSeats = this.seats;
    }

    if (isNumber(this.discount)) {
      data.discount = this.discount;
    }

    const input = new Input({
      spaceId: this.profile.inSpace.id,
      ...data,
    });

    this.updatingBilling = true;

    const mutation = UpdatePromotionMutation.New(input, {
      onSuccess: result => {
        Alert.success("User's billing has been updated successfully");
        this.updatingBilling = false;

        this.SetFreeSeatsPreviewNumber(result.freeSeats);
        this.SetDiscountPreviewNumber(result.discount);
      },
      onSysErr: () => {
        Alert.error('There was an error while updating user billing');
        this.updatingBilling = false;
      },
      onUserErr: () => {
        Alert.error('There was an error while updating user billing');
        this.updatingBilling = false;
      },
    });
    return mutation;
  };

  @action SetFreeSeatsPreviewNumber(freeSeats) {
    if (isNumber(freeSeats)) {
      this.freeSeatsPreview = freeSeats;
      return;
    }

    this.freeSeatsPreview =
      this.profile.billingSubscriptions.apps?.[0]?.seats?.freeSeats || 0;
  }

  @action SetDiscountPreviewNumber(discount) {
    if (isNumber(discount)) {
      this.discountPreview = discount;
      return;
    }

    this.discountPreview =
      this.profile.billingSubscriptions.apps?.[0]?.seats?.discount || 0;
  }

  @action SetEarlyAccess = value => {
    this.earlyAccess = String(value);
  };
}
